import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { CheckCircle2, XCircle, Loader2, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const getParam = (name) => {
  const params = new URLSearchParams(window.location.search);
  return params.get(name);
};

const clearParams = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete("session_id");
  url.searchParams.delete("canceled");
  window.history.replaceState({}, "", url.pathname);
};

export const PaymentStatus = () => {
  const { clear } = useCart();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState("idle"); // checking | paid | failed | canceled
  const [amount, setAmount] = useState(null);
  const [subEmail, setSubEmail] = useState("");
  const [subbed, setSubbed] = useState(false);
  const [subBusy, setSubBusy] = useState(false);

  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 8;
    const pollInterval = 2000;
    if (attempts >= maxAttempts) {
      setState("failed");
      return;
    }
    try {
      const res = await api.get(`/checkout/status/${sessionId}`);
      const data = res.data;
      if (data.payment_status === "paid") {
        setAmount(data.amount_total);
        setState("paid");
        clear();
        return;
      }
      if (data.status === "expired") {
        setState("failed");
        return;
      }
      setTimeout(() => pollStatus(sessionId, attempts + 1), pollInterval);
    } catch {
      setTimeout(() => pollStatus(sessionId, attempts + 1), pollInterval);
    }
  }, [clear]);

  useEffect(() => {
    const sessionId = getParam("session_id");
    const canceled = getParam("canceled");
    if (sessionId) {
      setOpen(true);
      setState("checking");
      pollStatus(sessionId);
    } else if (canceled) {
      setOpen(true);
      setState("canceled");
      clearParams();
    }
  }, [pollStatus]);

  const subscribe = async (e) => {
    e.preventDefault();
    if (subBusy || !subEmail) return;
    setSubBusy(true);
    try {
      const { data } = await api.post("/subscribe", { email: subEmail, source: "order_success" });
      setSubbed(true);
      toast.success(data.already_subscribed ? "Already on the list — thank you!" : "Subscribed! 🧶");
    } catch {
      toast.error("Could not subscribe");
    } finally {
      setSubBusy(false);
    }
  };

  const handleClose = (next) => {
    setOpen(next);
    if (!next) {
      clearParams();
      setState("idle");
      setSubEmail("");
      setSubbed(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-md"
        style={{ backgroundColor: "var(--bg-primary)" }}
        data-testid="payment-status-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-center" style={{ color: "var(--text-primary)" }}>
            {state === "paid" && "Thank you!"}
            {state === "checking" && "Confirming payment…"}
            {state === "failed" && "Something went wrong"}
            {state === "canceled" && "Checkout canceled"}
          </DialogTitle>
          <DialogDescription className="text-center font-body" style={{ color: "var(--text-secondary)" }}>
            {state === "paid" && (
              <>
                Your order is on its way to our studio. We can’t wait to hand-stitch your new friend!
                {amount && <> Total charged: <strong>${amount.toFixed(2)}</strong>.</>}
              </>
            )}
            {state === "checking" && "Hang tight, we're verifying your payment with Stripe."}
            {state === "failed" && "Your payment could not be confirmed. Please try again or contact us."}
            {state === "canceled" && "No worries — your cart is still saved for whenever you're ready."}
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-center pt-2 pb-4">
          {state === "paid" && <CheckCircle2 size={56} style={{ color: "var(--brand-sage)" }} />}
          {state === "checking" && <Loader2 size={56} className="animate-spin" style={{ color: "var(--brand-orange)" }} />}
          {state === "failed" && <XCircle size={56} style={{ color: "var(--brand-orange)" }} />}
          {state === "canceled" && <XCircle size={56} style={{ color: "var(--text-accent)" }} />}
        </div>

        {state === "paid" && !subbed && (
          <form
            onSubmit={subscribe}
            className="rounded-xl p-4 mb-3"
            style={{ backgroundColor: "var(--bg-secondary)", border: "1px dashed var(--border-soft)" }}
            data-testid="post-order-subscribe-form"
          >
            <div className="flex items-center gap-2 mb-2">
              <Mail size={16} style={{ color: "var(--brand-sage)" }} />
              <p className="font-script text-xl" style={{ color: "var(--text-accent)" }}>
                want a heads-up on new drops?
              </p>
            </div>
            <p className="text-xs mb-3" style={{ color: "var(--text-secondary)" }}>
              Join our list and we’ll send a gentle note when new plushies are ready.
            </p>
            <div className="flex gap-2">
              <input
                type="email"
                required
                value={subEmail}
                onChange={(e) => setSubEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 px-3 py-2 rounded-full text-sm outline-none"
                style={{ backgroundColor: "#fff", border: "1px solid var(--border-soft)" }}
                data-testid="post-order-subscribe-input"
              />
              <button
                type="submit"
                disabled={subBusy}
                className="btn-sage px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wide disabled:opacity-60"
                data-testid="post-order-subscribe-button"
              >
                {subBusy ? "…" : "Join"}
              </button>
            </div>
          </form>
        )}

        {state === "paid" && subbed && (
          <p
            className="font-script text-xl text-center mb-2"
            style={{ color: "var(--brand-sage)" }}
          >
            you’re in — thank you ♥
          </p>
        )}

        <button
          onClick={() => handleClose(false)}
          className="btn-sage w-full py-3 rounded-full text-sm font-semibold tracking-wide uppercase"
          data-testid="payment-status-close-button"
        >
          Close
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentStatus;
